import React, { FC, createContext, useCallback, useContext, useState, useMemo, useEffect } from 'react';
import { generatePath, matchPath, useHistory, useRouteMatch } from 'react-router-dom';
import { gqlArtist, gqlProfileStatusEnum, gqlSubscriptionStatusEnum } from '@minecraft.graphql-types';
import { AccountArtistFragment, ArtistSubscriptionFragment, UserContextFragment } from '@minecraft.graphql-operations';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { getEnvironment } from '@minecraft.environment';
import { useUserContext } from '@blocs.auth';
import { useTranslation } from '@blocs.i18n';
import { captureExceptionEvent } from '@blocs.sentry';
import { emailTokenVar } from 'lib/cache';
import { BrazeCustomAttributes, setCustomUserAttribute } from '@blocs.braze';
import {
  AccountSubscriptionStatusTypes,
  calculateAccountSubscriptionStatusType,
  getDefaultProfile,
  getSelectedArtist,
  isAdmin,
} from 'utils/helpers';
import routeHelper, { generateDefaultLink } from 'utils/routeHelper';
import StatusesForRedirect from 'config/constants/statusesForRedirect';
import { ALLOWED_PATHS_FOR_PAUSED_SUBSCRIPTION } from 'components/Accounts/constants/pausedSubscriptionPaths';

export type ID = string | number | null;

export interface ArtistContextInternalState {
  accountWasSwitchedByUser?: boolean;
  profileId: string;
  isSwitchingArtist: boolean;
}

export interface ArtistContextState<T extends gqlArtist | AccountArtistFragment = AccountArtistFragment>
  extends ArtistContextInternalState {
  artistId: number;
  profileStatusCode?: gqlProfileStatusEnum | null;
  artists: T[];
  currentArtist: T;
  currentProfile: T['profiles'][0];
  profiles: T['profiles'];
  isPersonal: boolean;
  isPlayableAgeSectionHidden: boolean;
  isLoggedIn: boolean;
  rosterInvitationToken: string | null;
  artistHasRepProfile?: boolean;
  accountSubscriptionStatusType?: AccountSubscriptionStatusTypes;
  shouldCreatePersonalProfile?: boolean;
  myAccount?: UserContextFragment;
  subscriptionStatus?: ArtistSubscriptionFragment['status'];
}

export interface ArtistContextMethods<T extends gqlArtist | AccountArtistFragment = AccountArtistFragment> {
  toggleArtist: (
    /** artistId to swtich context into */
    artistId: number,
    /** profileId to switch into, will default to the personal profile */
    profileId?: string,
    /** @deprecated - no longer used */
    refetchedAcct?: UserContextFragment
  ) => Promise<ArtistContextState<T>>;
  refetchData: (silent?: boolean) => Promise<ArtistContextState<T>>;
  reset: () => void;
}

export type ArtistContextType<T extends gqlArtist | AccountArtistFragment = AccountArtistFragment> =
  ArtistContextState<T> & ArtistContextMethods<T>;

const buildDefaultContext = () => ({
  artistId: null,
  profileId: null,
  artists: [],
  profiles: [],
  currentProfile: null,
  currentArtist: null,
  profileStatusCode: null,
  isSwitchingArtist: false,
  hasAugeoBenefits: false,
  isPlayableAgeSectionHidden: false,
  isPersonal: false,
  isLoggedIn: false,
  rosterInvitationToken: null,
  accountSubscriptionStatusType: null,
  subscriptionStatus: null,
  shouldCreatePersonalProfile: false,
  myAccount: null,
});

export const initialValues: ArtistContextType = {
  ...buildDefaultContext(),
  toggleArtist: () => {
    console.error('ArtistContext is not initialized');

    return null;
  },
  refetchData: async () => {
    console.error('ArtistContext is not initialized');

    return Promise.resolve(null);
  },
  reset: () => {
    console.error('ArtistContext is not initialized');

    return null;
  },
};

const ArtistContext = createContext(initialValues);

export const useArtistContext = () => {
  const context = useContext(ArtistContext);

  if (!context) {
    console.error('ArtistContext is not initialized');
  }

  return context;
};

const buildArtistContextState = ({
  myAccount,
  contextState,
  subscriptionStatus,
}: {
  myAccount: UserContextFragment;
  contextState: ArtistContextInternalState;
  subscriptionStatus: ArtistSubscriptionFragment['status'];
}): ArtistContextState => {
  if (!myAccount?.artists?.[0]?.profiles?.length) {
    return {
      ...buildDefaultContext(),
      isLoggedIn: !!myAccount,
      myAccount,
      subscriptionStatus,
      rosterInvitationToken: myAccount?.rosterInvitationToken ?? null,
    };
  }

  const { artists, emailResetToken } = myAccount;
  const { profileId, isSwitchingArtist, accountWasSwitchedByUser } = contextState;
  emailTokenVar({ verifyEmailToken: emailResetToken, message: null });
  const currentArtist: AccountArtistFragment = getSelectedArtist(artists, myAccount?.lastArtistId);
  // if passed profileId in url is in artist profile list - take that otherwise take artist default profile
  const currentProfile =
    currentArtist?.profiles?.find((p) => p?.profileId?.toString() === profileId?.toString()) ??
    getDefaultProfile(currentArtist);
  const shouldCreatePersonalProfile =
    !isAdmin() &&
    getEnvironment().FEATURE_TALENT_FORCE_PERSONAL_PROFILE === 'true' &&
    artists?.reduce((acc: boolean, a) => {
      return !(!acc && a?.profiles?.filter((p) => p?.isPersonal)?.length);
    }, false);
  const profileStatusCode =
    !myAccount?.subscription &&
    !currentArtist?.activeSubscription &&
    currentProfile?.profileStatus?.code === gqlProfileStatusEnum.ACTIVE
      ? gqlProfileStatusEnum.SELECT_MEMBERSHIP
      : (currentProfile?.profileStatus?.code as gqlProfileStatusEnum);

  return {
    ...buildDefaultContext(),
    isLoggedIn: true,
    rosterInvitationToken: myAccount?.rosterInvitationToken,
    currentArtist,
    artistId: currentArtist?.artistId,
    currentProfile,
    profileId: currentProfile?.profileId,
    isPersonal: currentProfile?.isPersonal,
    accountSubscriptionStatusType: calculateAccountSubscriptionStatusType(artists),
    artists,
    profiles: currentArtist?.profiles,
    isPlayableAgeSectionHidden: currentProfile?.isPlayableAgeLockedToTalent,
    shouldCreatePersonalProfile,
    artistHasRepProfile: currentArtist?.profiles?.some((prof) => !prof.isPersonal),
    profileStatusCode,
    isSwitchingArtist,
    accountWasSwitchedByUser,
    myAccount,
    subscriptionStatus,
  };
};

export const ArtistContextProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { showMessage } = useGlobalNotification();
  const { t } = useTranslation();
  const {
    params: { artistId: routeArtistId, profileId: routeProfileId, ...otherParams },
    path,
  } = useRouteMatch<{ artistId?: string; profileId?: string }>();
  const { push } = useHistory();
  const { myAccount, refreshTokens, switchAuthContext, subscription } = useUserContext();
  const [contextState, setContextState] = useState({
    accountWasSwitchedByUser: false,
    isSwitchingArtist: false,
    profileId: routeProfileId,
  });
  const computedContext = useMemo(
    () => buildArtistContextState({ myAccount, contextState, subscriptionStatus: subscription?.status ?? null }),
    [myAccount, contextState, subscription]
  );

  const refetchData = useCallback(async () => {
    // this should refresh the token from the user context and then rebuild the artist context
    const { profileId: stateProfileId, artistId: stateArtistId } = computedContext;

    setContextState((current) => ({
      ...current,
      isSwitchingArtist: true,
    }));

    try {
      const { myAccount: refreshedAccount, updateError, subscription: refreshedSubscription } = await refreshTokens();

      if (updateError) {
        throw updateError;
      }

      setContextState((currentState) => ({
        ...currentState,
        isSwitchingArtist: false,
      }));

      return buildArtistContextState({
        myAccount: refreshedAccount,
        contextState: computedContext,
        subscriptionStatus: refreshedSubscription?.status ?? null,
      });
    } catch (e) {
      console.error('ArtistContext - refetchData - failure', { error: e });

      captureExceptionEvent(e, {
        location: 'ArtistContext - refetchData - failure',
        stateArtistId,
        stateProfileId,
      });

      setContextState((currentState) => ({
        ...currentState,
        isSwitchingArtist: false,
      }));

      return { ...computedContext, isSwitchingArtist: false };
    }
  }, [computedContext, refreshTokens]);

  const toggleArtist = useCallback(
    async (artistId: number, profileIdParam?: string, freshAccount?: UserContextFragment) => {
      const {
        myAccount: stateAccount,
        profileId: stateProfileId,
        artistId: stateArtistId,
        currentArtist: stateArtist,
        subscriptionStatus: stateSubscriptionStatus,
      } = computedContext;
      const profileId = profileIdParam || stateProfileId;

      setContextState((current) => ({
        ...current,
        isSwitchingArtist: true,
      }));

      let newState = { ...contextState };
      // eslint-disable-next-line no-underscore-dangle
      let newAccount = freshAccount?.__typename === 'Account' ? freshAccount : stateAccount;
      let subscriptionStatus = stateSubscriptionStatus;
      const currentArtist =
        newAccount?.artists?.find((a) => a?.artistId?.toString() === artistId?.toString()) || stateArtist;

      // this just means the current context is already matching the requested, we cast all to string b/c
      // artistId is returned as a number in some places and a string in other endpoints from the api :(
      if (
        artistId?.toString() === stateArtistId?.toString() &&
        profileId?.toString() === stateProfileId?.toString() &&
        currentArtist?.profiles?.length > 0
      ) {
        // if trying to toggle to the current state, then we don't need to do anything
        newState = { ...newState, profileId: stateProfileId };
      } else if (
        artistId?.toString() === stateArtistId?.toString() &&
        profileId?.toString() !== stateProfileId?.toString() &&
        currentArtist?.profiles?.length > 0
      ) {
        // switch to a different profile on the same artist, we do not need to
        // switch auth context, just to update the current profile for the current context
        const newProfile = currentArtist.profiles.find((p) => p?.profileId?.toString() === profileId?.toString());

        if (newProfile) {
          // if a matching profile is found, set it as the new value
          newState = { ...newState, profileId };
        }
      } else {
        // try to switch into a new artist if the requested artistId is different
        try {
          const {
            myAccount: switchedAccount,
            updateError,
            subscription: switchedSubscription,
          } = await switchAuthContext({ artistId });
          const newArtist: AccountArtistFragment = getSelectedArtist(switchedAccount?.artists, artistId);
          const newProfile =
            newArtist?.profiles?.find((p) => p?.profileId?.toString() === profileId?.toString()) ??
            getDefaultProfile(newArtist);

          if (updateError) {
            throw updateError;
          }

          if (newProfile) {
            newState = {
              accountWasSwitchedByUser: true,
              isSwitchingArtist: false,
              profileId: newProfile?.profileId,
            };
          }

          if (switchedAccount) {
            newAccount = switchedAccount;
          }
          if (switchedSubscription) {
            subscriptionStatus = switchedSubscription?.status ?? null;
          }
        } catch (e) {
          console.error('ArtistContext - toggleArtist - switchContext failed', { error: e });
          captureExceptionEvent(e, {
            location: 'ArtistContext - toggleArtist - switchContext failed',
            artistId,
            stateArtistId,
            profileId,
            stateProfileId,
          });
          showMessage({
            color: 'danger',
            message: e?.message || t('common:toast.switchContextFailure'),
          });
        }
      }

      const finalContextState = { ...newState, isSwitchingArtist: false };

      const newContext = buildArtistContextState({
        myAccount: newAccount,
        contextState: finalContextState,
        subscriptionStatus,
      });

      if (StatusesForRedirect.find((s) => s === newContext.profileStatusCode)) {
        // if this artist/profile combo is in a special state, redirect them to the correct page
        const route = generateDefaultLink(newContext);

        push(route);
      } else if (
        (routeArtistId && newContext.artistId !== Number(routeArtistId)) ||
        (routeProfileId && newContext.profileId !== routeProfileId)
      ) {
        // this handles the case where a user is on a route with an `:artistId` or `:profileId` path parameter
        // and automatically will update the route so that the path parameters are correct for their context
        // this would typically happen if they are on their profile and then use the artist switcher to switch to a different artist
        const route = generatePath(path, {
          ...otherParams,
          artistId: newContext.artistId,
          profileId: newContext.profileId,
        });

        push(route);
      }

      setContextState(finalContextState);

      return newContext;
    },
    [
      computedContext,
      contextState,
      otherParams,
      path,
      push,
      routeArtistId,
      routeProfileId,
      showMessage,
      switchAuthContext,
      t,
    ]
  );

  const fullContext = useMemo(() => {
    return {
      refetchData,
      toggleArtist,
      reset: () => undefined,
      ...computedContext,
    };
  }, [refetchData, toggleArtist, computedContext]);

  useEffect(() => {
    const isRouteRestricted = !ALLOWED_PATHS_FOR_PAUSED_SUBSCRIPTION.some((route) =>
      matchPath(path, { path: route, exact: true, strict: true })
    );

    const isPausedSubscription = computedContext.subscriptionStatus === gqlSubscriptionStatusEnum.PAUSED;
    if (isRouteRestricted && isPausedSubscription) {
      push(routeHelper.membershipOverview.link() as string);
    }
  }, [computedContext.subscriptionStatus, path, push]);

  useEffect(() => {
    // these routes are exempt from redirecting to the Paywall even if the user has no subscription
    const isRedirectExemptRoute = !![
      '/profile/:profileId/subscriptions',
      '/profile/verify-age',
      '/profile/create',
      '/profile/create/:profileId',
      '/default',
      '/default-redirect',
      '/maintenance',
      '/public-profile/:guid',
      '/login',
      '/',
      '/verify-email',
    ].some((route) =>
      matchPath(path, {
        path: route,
        exact: true,
        strict: true,
      })
    );

    const currentProfileId = computedContext?.currentProfile?.profileId;
    const {
      activeSubscription: currentArtistActiveSubscription,
      previousSubscription: currentArtistPreviousSubscription,
    } = computedContext?.currentArtist ?? {};
    const shouldRedirectToPaywall =
      !(currentArtistPreviousSubscription || currentArtistActiveSubscription) &&
      currentProfileId &&
      !isRedirectExemptRoute;
    // redirect to checkout if the user has no subscription
    if (shouldRedirectToPaywall) {
      push(routeHelper.profileSubscriptions.link(currentProfileId) as string);
    }
  }, [computedContext, path, push]);

  useEffect(() => {
    const { currentProfile } = computedContext;
    if (currentProfile) {
      setCustomUserAttribute(
        BrazeCustomAttributes.B21_GENDER,
        currentProfile?.genderAppearances?.map((gender) => gender?.name).join(', ') ?? ''
      );
      setCustomUserAttribute(
        BrazeCustomAttributes.WORKING_LOCATIONS,
        currentProfile?.workingLocations?.map((location) => location?.name).join(', ') ?? ''
      );
      setCustomUserAttribute(
        BrazeCustomAttributes.ETHNIC_APPEARANCE,
        currentProfile?.ethnicAppearances?.map((ethnic) => ethnic?.name).join(', ') ?? ''
      );
      setCustomUserAttribute(BrazeCustomAttributes.MIN_PLAYABLE_AGE, currentProfile?.playableAgeMin);
      setCustomUserAttribute(BrazeCustomAttributes.MAX_PLAYABLE_AGE, currentProfile?.playableAgeMax);
    }
  }, [computedContext.currentProfile]);

  return <ArtistContext.Provider value={fullContext}>{children}</ArtistContext.Provider>;
};

export default ArtistContext;
